import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";
import {
  ArrowForward,
  Assessment,
  Save,
  School,
  TrendingUp
} from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  getAbsiteDiagnosticReport,
  resetAbsiteDiagnosticProfile,
  resetAbsiteDiagnosticReport,
  saveAbsiteDiagnosticProfile
} from "../../actions";
import Menu from "../../pages/menu/menu";
import {
  trackDiagnosticProfileCaptured,
  trackDiagnosticReportViewed
} from "../../utils/tracking";
import "./absite-diagnostic.scss";

const PGY_LEVELS = ["PGY-1", "PGY-2", "PGY-3", "PGY-4", "PGY-5", "Research"];

const formatPercent = (value) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return "0%";
  }

  return `${Math.round(Number(value))}%`;
};

export default function AbsiteDiagnosticReportPage() {
  const { quizId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const reportTrackedRef = useRef(false);
  const profileTrackedRef = useRef(false);
  const [programCode, setProgramCode] = useState("");
  const [pgyLevel, setPgyLevel] = useState("");
  const [profileSaved, setProfileSaved] = useState(false);

  const reportState = useSelector((state) => state.absiteDiagnosticReport);
  const profileState = useSelector((state) => state.absiteDiagnosticProfile);
  const report = reportState?.diagnosticReport;
  const weakestAreas = report?.weakestAreas || [];
  const studyPlan = report?.studyPlan || [];

  useEffect(() => {
    dispatch(getAbsiteDiagnosticReport(quizId));

    return () => {
      dispatch(resetAbsiteDiagnosticReport());
      dispatch(resetAbsiteDiagnosticProfile());
    };
  }, [dispatch, quizId]);

  useEffect(() => {
    if (report && !reportTrackedRef.current) {
      reportTrackedRef.current = true;
      trackDiagnosticReportViewed({
        quizId,
        completed: report.completed,
        attemptCount: report.attemptCount,
        weakestAreasCount: weakestAreas.length
      });
    }
  }, [quizId, report, weakestAreas.length]);

  useEffect(() => {
    const savedProfile = profileState?.diagnosticProfile;
    if (savedProfile && !profileTrackedRef.current) {
      profileTrackedRef.current = true;
      setProfileSaved(true);
      trackDiagnosticProfileCaptured({
        quizId,
        programCode: savedProfile.programCode,
        pgyLevel: savedProfile.pgyLevel
      });
    }
  }, [profileState, quizId]);

  const onSaveProfile = () => {
    profileTrackedRef.current = false;
    setProfileSaved(false);
    dispatch(saveAbsiteDiagnosticProfile({
      quizId,
      profile: {
        programCode,
        pgyLevel
      }
    }));
  };

  const goToQuiz = () => history.push(`/exam/${quizId}/1`);
  const goToPractice = () => history.push("/create-quiz");

  return (
    <Box component="div" className="diagnostic-page">
      <Menu />
      <Box component="main" className="diagnostic-shell diagnostic-report-shell">
        <Box component="section" className="diagnostic-report-header">
          <Box component="div" className="diagnostic-eyebrow">
            ABSITE readiness
          </Box>
          <Typography component="h1" className="diagnostic-title">
            Diagnostic report
          </Typography>
          <Typography component="p" className="diagnostic-copy">
            Review your score, strongest next moves, and seven-day plan.
          </Typography>
        </Box>

        {reportState?.loading && !report && (
          <Box component="div" className="diagnostic-loading">
            <CircularProgress />
          </Box>
        )}

        {reportState?.errMsg && (
          <Box component="div" className="diagnostic-error">
            {reportState.errMsg}
          </Box>
        )}

        {report && (
          <>
            <Box component="section" className="diagnostic-report-grid">
              <Paper elevation={0} className="diagnostic-score-panel">
                <Assessment />
                <Typography component="h2">Score</Typography>
                <Box component="div" className="diagnostic-score">
                  {formatPercent(report.correctPercentage)}
                </Box>
                <Typography component="p">
                  {report.correctCount} correct out of {report.attemptCount} attempted
                </Typography>
              </Paper>

              <Paper elevation={0} className="diagnostic-status-panel">
                <TrendingUp />
                <Typography component="h2">
                  {report.completed ? "Three weakest areas" : "Diagnostic in progress"}
                </Typography>
                {report.completed ? (
                  <Box component="div" className="diagnostic-weak-list">
                    {weakestAreas.map((area) => (
                      <Box component="div" className="diagnostic-weak-item" key={`${area.category}-${area.subcategory}`}>
                        <span>{area.name}</span>
                        <strong>{formatPercent(area.percentageCorrect)}</strong>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box component="div" className="diagnostic-incomplete">
                    <Typography component="p">
                      Complete the diagnostic to unlock weakest areas and the study plan.
                    </Typography>
                    <Button
                      color="primary"
                      variant="outlined"
                      onClick={goToQuiz}
                      startIcon={<ArrowForward />}
                    >
                      Resume
                    </Button>
                  </Box>
                )}
              </Paper>

              <Paper elevation={0} className="diagnostic-profile-panel">
                <School />
                <Typography component="h2">Program context</Typography>
                <Box component="div" className="diagnostic-profile-form">
                  <TextField
                    label="Program code"
                    variant="outlined"
                    value={programCode}
                    onChange={(event) => setProgramCode(event.target.value)}
                    size="small"
                  />
                  <TextField
                    select
                    label="PGY"
                    variant="outlined"
                    value={pgyLevel}
                    onChange={(event) => setPgyLevel(event.target.value)}
                    size="small"
                  >
                    <MenuItem value="">
                      Select
                    </MenuItem>
                    {PGY_LEVELS.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={onSaveProfile}
                    startIcon={profileState?.loading ? <CircularProgress size={18} /> : <Save />}
                    disabled={profileState?.loading || (!programCode && !pgyLevel)}
                  >
                    Save
                  </Button>
                </Box>
                {profileSaved && (
                  <Box component="div" className="diagnostic-save-confirmation">
                    Saved
                  </Box>
                )}
                {profileState?.errMsg && (
                  <Box component="div" className="diagnostic-error diagnostic-profile-error">
                    {profileState.errMsg}
                  </Box>
                )}
              </Paper>
            </Box>

            <Paper elevation={0} className="diagnostic-plan-panel">
              <Typography component="h2">Seven-day study plan</Typography>
              <Box component="div" className="diagnostic-plan-list">
                {studyPlan.map((day) => (
                  <Box component="div" className="diagnostic-plan-day" key={day.day}>
                    <Box component="div" className="diagnostic-plan-number">
                      Day {day.day}
                    </Box>
                    <Box component="div">
                      <strong>{day.focus}</strong>
                      <p>{day.task}</p>
                    </Box>
                  </Box>
                ))}
              </Box>
              {report.completed && (
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={goToPractice}
                  startIcon={<ArrowForward />}
                >
                  Build practice quiz
                </Button>
              )}
            </Paper>
          </>
        )}

        <Typography component="p" className="diagnostic-disclaimer">
          QLearn is not affiliated with or endorsed by the American Board of Surgery.
        </Typography>
      </Box>
    </Box>
  );
}
